import { FURNITURE_CONFIG } from '~/components/ThreeDModel/furnitureConfig'
import {
  RackColumnConfiguration,
  RackZoneType,
  SHELF_SPACING_PRESETS,
} from '~/types/rackConfigurationTypes'
import { calculateRackColumnLayout } from '~/utils/rackColumnLayout'
import { calculateOptimalDrawerConfig } from '~/config/rackTemplates'
import { computeSidePanelDimensions } from '~/utils/computeSidePanelDimensions'
import { computeTopPanelDimensions } from '~/utils/computeTopPanelDimensions'
import { computeColumnDividerDimensions } from '~/utils/computeColumnDividerDimensions'
import type { PanelPart } from '~/context/wardrobePartsContext'

const { panelThickness, panelSpacing, drawerInsidePanelsOffset } =
  FURNITURE_CONFIG

export interface RackPartsArgs {
  width: number
  height: number
  depth: number
  plintHeight: number
  columnConfigurations?: RackColumnConfiguration[]
  /** Per-column widths (cm); falls back to calculateRackColumnLayout(width). */
  columnWidths?: number[]
}

export interface RackParts {
  panels: PanelPart[]
  /** Number of door facades (for the pricing `doorsNumber` option). */
  doorsNumber: number
  /** One zone record per column (templateId + shelf/drawer counts) for backend. */
  zones: Record<string, number | boolean | string>[]
}

/** The five PAL panels of one drawer box (front + bottom + two sides + back). */
function drawerPanels(
  drawerWidth: number,
  drawerHeight: number,
  drawerDepth: number
): PanelPart[] {
  const innerHeight = drawerHeight - panelSpacing
  const innerWidth = drawerWidth - panelThickness * 2
  const innerDepth = drawerDepth - panelThickness * 2
  const sideHeight = innerHeight - drawerInsidePanelsOffset

  return [
    { width: drawerWidth, height: innerHeight, quantity: 1 }, // front
    { width: innerWidth, height: innerDepth, quantity: 1 }, // bottom
    { width: innerDepth, height: sideHeight, quantity: 2 }, // left + right
    { width: innerWidth, height: sideHeight, quantity: 1 }, // back
  ]
}

interface AdjustedZone {
  originalZone: RackColumnConfiguration['zones'][number]
  adjustedBottomY: number
  adjustedTopY: number
  adjustedHeight: number
}

/**
 * Master shelf grid — mirrors RackColumn's masterShelfPositions memo. Shelves
 * across every column snap to this shared grid for symmetric alignment, so the
 * priced shelf count matches the rendered one (not the raw zone.shelfCount).
 */
function computeMasterGrid(
  config: RackColumnConfiguration,
  columnHeight: number
): { positions: number[]; spacing: number } {
  if (!config.zones || config.zones.length === 0) {
    return { positions: [], spacing: 0 }
  }

  const masterGridHeight = columnHeight
  const preset =
    SHELF_SPACING_PRESETS[config.shelfSpacingPreset ?? 'large']
  const MIN_SHELF_SPACING = preset.min
  const MAX_SHELF_SPACING = preset.max
  const OPTIMAL_SHELF_SPACING = preset.optimal

  let count = Math.round(masterGridHeight / OPTIMAL_SHELF_SPACING) - 1
  if (count < 1) count = 1
  let spacing = masterGridHeight / (count + 1)

  while (spacing < MIN_SHELF_SPACING && count > 1) {
    count--
    spacing = masterGridHeight / (count + 1)
  }
  while (spacing > MAX_SHELF_SPACING) {
    const newSpacing = masterGridHeight / (count + 2)
    if (newSpacing >= MIN_SHELF_SPACING) {
      count++
      spacing = newSpacing
    } else {
      break
    }
  }

  const positions: number[] = []
  for (let i = 1; i <= count; i++) positions.push(spacing * i)
  return { positions, spacing }
}

/**
 * Snap door/drawer zone boundaries to the master grid — mirrors RackColumn's
 * adjustedZones memo, including the drawer-count recalculation after a snap.
 */
function computeAdjustedZones(
  config: RackColumnConfiguration,
  positions: number[],
  spacing: number
): AdjustedZone[] {
  const zones = config.zones ?? []
  if (zones.length === 0) return []

  const totalZonesHeight = zones.reduce((sum, z) => sum + z.height, 0)
  const adjusted: AdjustedZone[] = []
  let currentTopY = totalZonesHeight
  for (const zone of zones) {
    const bottomY = currentTopY - zone.height
    adjusted.push({
      originalZone: zone,
      adjustedBottomY: bottomY,
      adjustedTopY: currentTopY,
      adjustedHeight: zone.height,
    })
    currentTopY = bottomY
  }

  const MAX_DOOR_ZONE_HEIGHT = 130
  const MIN_ADJACENT_ZONE_HEIGHT = 28
  const MIN_DOOR_HEIGHT = 28 // getZoneMinHeight() is a constant 28 in RackColumn.

  const findBestGridPosition = (
    targetValue: number,
    doorBottomY: number,
    adjacentTopY: number
  ): number | null => {
    const valid: Array<{ pos: number; distance: number }> = []
    for (const pos of positions) {
      const doorHeight = pos - doorBottomY
      const adjacentHeight = adjacentTopY - pos
      if (
        doorHeight >= MIN_DOOR_HEIGHT &&
        doorHeight <= MAX_DOOR_ZONE_HEIGHT &&
        adjacentHeight >= MIN_ADJACENT_ZONE_HEIGHT
      ) {
        valid.push({ pos, distance: Math.abs(pos - targetValue) })
      }
    }
    if (valid.length === 0) return null
    valid.sort((a, b) => {
      if (Math.abs(a.distance - b.distance) < 2.0) return b.pos - a.pos
      return a.distance - b.distance
    })
    return valid[0].distance <= spacing * 1.5 ? valid[0].pos : null
  }

  for (let i = 0; i < adjusted.length; i++) {
    const zone = adjusted[i].originalZone
    const hasDoorOverZone =
      config.doors?.some((door) => door.zoneIndices.includes(i)) ?? false
    const shouldSnapToGrid =
      (hasDoorOverZone || zone.type === RackZoneType.DRAWERS) && i > 0
    if (!shouldSnapToGrid) continue

    const originalTop = adjusted[i].adjustedTopY
    const bottomY = adjusted[i].adjustedBottomY
    const adjacentTopY = adjusted[i - 1].adjustedTopY
    const gridCandidate = findBestGridPosition(
      originalTop,
      bottomY,
      adjacentTopY
    )
    if (gridCandidate === null) continue

    const newHeight = gridCandidate - bottomY
    const newAdjacentHeight = adjacentTopY - gridCandidate
    adjusted[i].adjustedTopY = gridCandidate
    adjusted[i].adjustedHeight = newHeight
    adjusted[i - 1].adjustedBottomY = gridCandidate
    adjusted[i - 1].adjustedHeight = newAdjacentHeight

    if (
      zone.type === RackZoneType.DRAWERS &&
      zone.drawerCount &&
      zone.drawerCount > 0
    ) {
      const { count, heights } = calculateOptimalDrawerConfig(
        newHeight,
        15,
        25,
        20
      )
      adjusted[i].originalZone = {
        ...zone,
        height: newHeight,
        drawerCount: count,
        drawerHeights: heights,
      }
    }
  }

  return adjusted
}

/** Door layout for a column — mirrors RackColumn's doorType memo. */
function computeDoorType(
  config: RackColumnConfiguration,
  columnWidth: number
): 'none' | 'single' | 'split' {
  const doorConfig = config.doors?.[0]
  if (!doorConfig) return 'none'
  if (doorConfig.type === 'single') {
    return columnWidth >= 60 ? 'split' : 'single'
  }
  if (doorConfig.type === 'split') return 'split'
  return 'none'
}

/** Facade height for the column's door — mirrors RackColumn's doorConfig memo. */
function computeDoorHeight(
  config: RackColumnConfiguration,
  adjusted: AdjustedZone[],
  columnHeight: number,
  plintHeight: number
): number {
  const hasDoors = !!config.doors && config.doors.length > 0
  if (!hasDoors || adjusted.length === 0) return columnHeight - plintHeight

  const zoneIndices = config.doors?.[0]?.zoneIndices ?? []
  if (zoneIndices.length === 0) return columnHeight - plintHeight

  let minBottomY = Infinity
  let maxTopY = -Infinity
  for (const zoneIndex of zoneIndices) {
    if (zoneIndex >= 0 && zoneIndex < adjusted.length) {
      minBottomY = Math.min(minBottomY, adjusted[zoneIndex].adjustedBottomY)
      maxTopY = Math.max(maxTopY, adjusted[zoneIndex].adjustedTopY)
    }
  }

  let doorHeight = maxTopY - minBottomY
  const isHalfOpenHalfClosed =
    config.templateId === 'HALF_OPEN_HALF_CLOSED' ||
    config.templateId === 'OPEN_AND_SMALL_BOTTOM_CLOSED' ||
    config.templateId === 'OPEN_AND_BOTTOM_CLOSED'
  if (isHalfOpenHalfClosed && maxTopY > 0) doorHeight += 1
  return doorHeight
}

/** A rack shelf panel — Shelf(columnWidth-1, columnDepth); face is (w-1)×(D-4). */
function shelfPanel(columnWidth: number, columnDepth: number): PanelPart {
  return { width: columnWidth - 1, height: columnDepth - 4, quantity: 1 }
}

/**
 * Enumerate every PAL panel the rack 3D scene draws — outer carcass (sides, top,
 * plinth), full-width back + bottom shelf, dividers, and per column the shelves
 * (on the shared master grid), drawer boxes and door facades — so the pricing
 * quote matches the rendered model.
 *
 * Mirrors: products/rack/builder.tsx (carcass + back + bottom + dividers) and
 * parts/RackColumn.tsx + rack-zones/RackZoneRenderer.tsx (per-column zones).
 * Handles/hinges/runners are metal and excluded.
 */
export function computeRackParts(args: RackPartsArgs): RackParts {
  const { width, height: H, depth: D, plintHeight } = args
  const layout = calculateRackColumnLayout(width)
  const columns = layout.columnCount
  const widths =
    args.columnWidths && args.columnWidths.length === columns
      ? args.columnWidths
      : layout.columnWidths
  const configs = args.columnConfigurations ?? []

  const panels: PanelPart[] = []
  const zones: Record<string, number | boolean | string>[] = []
  let doorsNumber = 0

  // Outer carcass: two side panels + top + plinth (SidePanels + TopAndPlinth).
  const sides = computeSidePanelDimensions({
    width,
    height: H,
    depth: D,
    isNewCollection: false,
    isSingleNarrowColumn: false,
    isTwoSingleDoorsException: false,
    isMirrored: false,
  })
  const top = computeTopPanelDimensions({
    width,
    depth: D,
    isNewCollection: false,
  })
  panels.push(
    { ...sides.left, quantity: 1 },
    { ...sides.right, quantity: 1 },
    { width: top.width, height: top.height, quantity: 1 }
  )
  // Plinth: racks default to plintHeight 0 (no plinth) — skip the degenerate
  // zero-height panel the backend rejects as non-positive.
  if (plintHeight > 0) {
    panels.push({ width, height: plintHeight, quantity: 1 })
  }

  // Full-width back panel + full-width bottom shelf (drawn once by the builder).
  panels.push({ width, height: H, quantity: 1 })
  panels.push({ width: width - 1, height: D - 4, quantity: 1 })

  // Dividers between adjacent columns (builder hardcodes plintHeight=2).
  const dividerDims = computeColumnDividerDimensions({
    columnHeight: H,
    columnDepth: D,
    plintHeight: 2,
  })
  for (let i = 0; i < columns - 1; i++) {
    panels.push({
      width: dividerDims.width,
      height: dividerDims.height,
      quantity: 1,
    })
  }

  for (let i = 0; i < columns; i++) {
    const columnWidth = widths[i]
    const config = configs[i]
    if (!columnWidth || !config) continue

    const { positions, spacing } = computeMasterGrid(config, H)
    const adjusted = computeAdjustedZones(config, positions, spacing)

    let shelfCountTotal = 0
    let drawerCountTotal = 0

    adjusted.forEach((az, index) => {
      const zone = az.originalZone
      const zoneBottomY = az.adjustedBottomY
      const zoneHeight = az.adjustedHeight
      const isShelvesZone =
        zone.type === RackZoneType.SHELVES ||
        zone.type === RackZoneType.SHELVES_FIXED

      const hasDoorOverZone =
        config.doors?.some((door) => door.zoneIndices.includes(index)) ?? false
      const isTopZone = index === 0
      const nextZone = config.zones[index + 1]
      const needsSeparatorShelf =
        !!nextZone && zone.type !== nextZone.type && isShelvesZone
      const shouldRenderShelf =
        (hasDoorOverZone && !isTopZone) || needsSeparatorShelf

      // Ceiling / separator shelf (RackZoneRenderer topShelf).
      if (shouldRenderShelf) {
        panels.push(shelfPanel(columnWidth, D))
        shelfCountTotal++
      }

      if (isShelvesZone) {
        if (positions.length > 0) {
          const zoneTop = zoneBottomY + zoneHeight
          const SHELF_MARGIN = 5
          const shelvesInZone = positions.filter(
            (pos) =>
              pos > zoneBottomY + SHELF_MARGIN && pos < zoneTop - SHELF_MARGIN
          )
          shelvesInZone.forEach(() => {
            panels.push(shelfPanel(columnWidth, D))
            shelfCountTotal++
          })
        } else if (zone.shelfCount && zone.shelfCount > 0) {
          for (let s = 0; s < zone.shelfCount; s++) {
            panels.push(shelfPanel(columnWidth, D))
            shelfCountTotal++
          }
        }
      } else if (zone.type === RackZoneType.DRAWERS) {
        if (zone.drawerCount && zone.drawerCount > 0 && zone.drawerHeights) {
          for (let d = 0; d < zone.drawerCount; d++) {
            panels.push(...drawerPanels(columnWidth, zone.drawerHeights[d], D))
            drawerCountTotal++
          }
        }
      }
    })

    // Door facades (RackColumn doors).
    const doorType = computeDoorType(config, columnWidth)
    if (doorType !== 'none') {
      const doorHeight = computeDoorHeight(config, adjusted, H, plintHeight)
      if (doorType === 'split') {
        const doorWidth = columnWidth / 2 - panelSpacing / 2
        panels.push({ width: doorWidth, height: doorHeight, quantity: 2 })
        doorsNumber += 2
      } else {
        const doorWidth = columnWidth - panelSpacing
        panels.push({ width: doorWidth, height: doorHeight, quantity: 1 })
        doorsNumber += 1
      }
    }

    zones.push({
      type: config.templateId ?? 'rack',
      height: H,
      shelves: shelfCountTotal,
      drawers: drawerCountTotal,
      hasShelfAtTop: false,
    })
  }

  return { panels, doorsNumber, zones }
}
